import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Plus, X, Loader2, ChevronDown, Check, UserPlus, Sparkles, BadgeDollarSign } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function InventoryLookup() {
  const nav = useNavigate();
  const [consoles, setConsoles] = useState([]);
  const [console_, setConsole] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);

  // checkout state
  const [coMode, setCoMode] = useState("existing"); // existing | new
  const [coPhone, setCoPhone] = useState("");
  const [coGuestName, setCoGuestName] = useState("");
  const [coGuestEmail, setCoGuestEmail] = useState("");
  const [coGuestPhone, setCoGuestPhone] = useState("");
  const [coSignup, setCoSignup] = useState({ first_name: "", last_name: "", birth_month: 1, phone: "", email: "" });
  const [coNote, setCoNote] = useState("");
  const [coBusy, setCoBusy] = useState(false);
  const [coError, setCoError] = useState("");
  // Member perks (live-fetched after phone is entered)
  const [perks, setPerks] = useState(null); // {first_name, points, trade_credit, discount_tiers}
  const [perksLoading, setPerksLoading] = useState(false);
  const [perksError, setPerksError] = useState("");
  const [pointsDiscount, setPointsDiscount] = useState(0); // dollars (0=none)
  const [creditUsed, setCreditUsed] = useState(0);

  useEffect(() => {
    api.get("/shop/consoles").then((r) => setConsoles(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {};
      const c = overrides.console_ ?? console_;
      const query = overrides.q ?? q;
      if (c) params.console = c;
      if (query.trim()) params.q = query.trim();
      const r = await api.get("/shop/items", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (it) => {
    // Game items unique → don't allow re-adding the same id; non-game can stack up to qty.
    if (!it.is_non_game && cart.some((c) => c.id === it.id)) return;
    if (it.is_non_game) {
      const existing = cart.find((c) => c.id === it.id);
      const count = existing ? cart.filter((c) => c.id === it.id).length : 0;
      if (count >= it.quantity) return;
    }
    setCart((p) => [...p, it]);
    setCartOpen(true);
  };
  const removeFromCart = (idx) => setCart((p) => p.filter((_, i) => i !== idx));

  const subtotal = useMemo(() => cart.reduce((s, it) => s + Number(it.price), 0), [cart]);

  // Live perks fetch: when the customer types a member phone, pull their balance.
  useEffect(() => {
    if (!checkout || coMode !== "existing") return;
    const ph = coPhone.trim();
    if (ph.length < 7) {
      setPerks(null); setPerksError(""); setPointsDiscount(0); setCreditUsed(0);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPerksLoading(true); setPerksError("");
      try {
        const r = await api.get(`/members/${encodeURIComponent(ph)}/checkout-perks`);
        if (cancelled) return;
        setPerks(r.data);
        setPointsDiscount(0); setCreditUsed(0);
      } catch (e) {
        if (cancelled) return;
        setPerks(null);
        setPerksError(e.response?.status === 404 ? "No AB member found with that phone." : (e.response?.data?.detail || "Lookup failed"));
      } finally {
        if (!cancelled) setPerksLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [coPhone, checkout, coMode]);

  // Available redemption tiers (filtered by balance + cart subtotal)
  const availableTiers = useMemo(() => {
    if (!perks) return [];
    return perks.discount_tiers.filter((t) => perks.points >= t.points && subtotal >= t.amount);
  }, [perks, subtotal]);
  const maxCredit = useMemo(() => {
    if (!perks) return 0;
    return Math.max(0, Math.min(perks.trade_credit, Math.floor(subtotal - pointsDiscount)));
  }, [perks, subtotal, pointsDiscount]);
  // Clamp creditUsed if subtotal/pointsDiscount changes
  useEffect(() => {
    if (creditUsed > maxCredit) setCreditUsed(maxCredit);
  }, [maxCredit, creditUsed]);
  const finalTotal = Math.max(0, subtotal - pointsDiscount - creditUsed);
  // µPoints earn estimate: 25 µPts per $1 of net-of-µPoint-discount subtotal
  // (matches /pos/sale + Complete handlers). Trade credit dollars DO count.
  const estimatedEarn = perks ? Math.floor(Math.max(0, subtotal - pointsDiscount) * 25) : 0;

  const submitReservation = async (e) => {
    e?.preventDefault();
    setCoError(""); setCoBusy(true);
    try {
      const member = coMode === "existing"
        ? { phone: coPhone.trim(), create_account: false }
        : { ...coSignup, birth_month: Number(coSignup.birth_month), create_account: true };
      const payload = {
        items: cart.map((c) => ({ inventory_id: c.id, quantity: 1 })),
        member,
        customer_name: coMode === "new"
          ? `${coSignup.first_name} ${coSignup.last_name}`.trim()
          : (coGuestName || undefined),
        customer_phone: coMode === "existing" ? coPhone.trim() : coSignup.phone,
        customer_email: coMode === "existing"
          ? (coGuestEmail || undefined)  // will fall back to member email server-side
          : coSignup.email,
        notes: coNote || undefined,
        points_discount_amount: pointsDiscount || 0,
        trade_credit_used: creditUsed || 0,
      };
      // Guest fallback: no member, no signup — just use the guest fields
      if (coMode === "existing" && !coPhone.trim()) {
        payload.member = { create_account: false };
        payload.customer_name = coGuestName;
        payload.customer_email = coGuestEmail;
        payload.customer_phone = coGuestPhone;
      }
      const r = await api.post("/purchase-requests", payload);
      nav(`/shop/reservation/${r.data.id}`);
    } catch (err) {
      setCoError(err.response?.data?.detail || "Failed to submit reservation");
    } finally {
      setCoBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Shop</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-3">Shop Inventory</h1>
        <p className="text-sm max-w-xl mb-8" style={{ color: "var(--ab-text-muted)" }}>
          Browse what's on the shelf right now. Reserve items by adding them to your cart and submitting — we'll email you back with a pickup window.
        </p>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 mb-6 items-stretch">
          <div className="relative">
            <select
              className="input-terminal pr-10 appearance-none"
              value={console_}
              onChange={(e) => { setConsole(e.target.value); load({ console_: e.target.value }); }}
              data-testid="shop-console-filter"
              style={{ minWidth: 220 }}
            >
              <option value="">All Consoles ({consoles.reduce((s, c) => s + c.count, 0)})</option>
              {consoles.map((c) => (
                <option key={c.console} value={c.console}>{c.console} ({c.count})</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ab-text-dim)" }} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="surface flex items-stretch flex-1 min-w-[260px]">
            <input
              className="flex-1 bg-transparent border-0 px-4 py-3 outline-none font-mono"
              placeholder="Search title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="shop-search-input"
            />
            <button type="submit" className="px-5" style={{ background: "var(--ab-surface-2)" }} data-testid="shop-search-btn">
              <Search size={16} />
            </button>
          </form>
          <button
            type="button"
            onClick={() => setCartOpen((v) => !v)}
            className="btn-primary inline-flex items-center gap-2 relative"
            data-testid="shop-cart-toggle"
          >
            <ShoppingCart size={16} /> Cart
            {cart.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center text-[10px] font-mono font-bold rounded-full" style={{ background: "var(--ab-accent-red)", color: "#fff", width: 18, height: 18 }}>{cart.length}</span>
            )}
          </button>
        </div>

        {/* Results grid + sticky cart */}
        <div className="grid lg:grid-cols-4 gap-4">
          <div className={`${cartOpen ? "lg:col-span-3" : "lg:col-span-4"}`}>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 9 }).map((_, i) => <div key={i} className="surface p-5 animate-pulse" style={{ minHeight: 120 }} />)}
              </div>
            ) : items.length === 0 ? (
              <div className="surface p-8 text-sm" data-testid="shop-empty" style={{ color: "var(--ab-text-muted)" }}>
                No items match. Try clearing filters.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shop-results">
                {items.map((it, i) => {
                  const inCart = cart.some((c) => c.id === it.id);
                  return (
                    <div key={it.id} className="surface p-5 flex flex-col gap-3 fade-in" style={{ animationDelay: `${i * 0.02}s` }} data-testid="shop-result-card">
                      <div className="flex items-center justify-between">
                        <span className="label-eyebrow truncate" style={{ maxWidth: "70%" }}>{it.console}</span>
                        <span className="text-xs font-mono" style={{ color: "var(--ab-text-dim)" }}>
                          {it.is_non_game ? "ACCESSORY" : (it.components === "Game in Box with Manual" ? "CIB" : it.components === "Game with missing Components" ? "Loose" : (it.components || "Loose"))}
                        </span>
                      </div>
                      <div className="font-display text-lg font-bold leading-tight line-clamp-2" title={it.title}>{it.title}</div>
                      <div className="flex items-end justify-between mt-auto pt-1">
                        <span className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.condition || ""}</span>
                        <span className="font-mono font-bold text-2xl" style={{ color: "var(--ab-primary)" }}>${Number(it.price).toFixed(it.is_non_game ? 2 : 0)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => addToCart(it)}
                        disabled={inCart && !it.is_non_game}
                        className={`mt-2 inline-flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${inCart && !it.is_non_game ? "opacity-60 cursor-not-allowed" : "hover:bg-[var(--ab-surface-2)]"}`}
                        style={{ borderColor: "var(--ab-border)" }}
                        data-testid={`shop-add-${it.id}`}
                      >
                        {inCart && !it.is_non_game ? <><Check size={12} /> In Cart</> : <><Plus size={12} /> Add to Cart</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sticky cart */}
          {cartOpen && (
            <aside className="surface p-5 self-start sticky top-6 max-h-[80vh] flex flex-col" data-testid="shop-cart-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow inline-flex items-center gap-2" style={{ color: "var(--ab-primary)" }}>
                  <ShoppingCart size={12} /> Your Cart
                </div>
                <button onClick={() => setCartOpen(false)} className="text-xs label-eyebrow" data-testid="shop-cart-close">Hide</button>
              </div>
              {cart.length === 0 ? (
                <div className="text-sm py-4" style={{ color: "var(--ab-text-muted)" }}>Nothing in your cart yet.</div>
              ) : (
                <div className="flex-1 overflow-auto -mx-2 divide-y" style={{ borderColor: "var(--ab-border)" }}>
                  {cart.map((it, idx) => (
                    <div key={idx} className="flex items-start gap-2 px-2 py-2" data-testid="shop-cart-row">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{it.title}</div>
                        <div className="text-[11px]" style={{ color: "var(--ab-text-dim)" }}>{it.console}</div>
                      </div>
                      <div className="font-mono text-sm font-bold" style={{ color: "var(--ab-primary)" }}>${Number(it.price).toFixed(it.is_non_game ? 2 : 0)}</div>
                      <button onClick={() => removeFromCart(idx)} className="inline-flex items-center justify-center" style={{ width: 22, height: 22, border: "1px solid var(--ab-danger)", color: "var(--ab-danger)" }} data-testid={`shop-cart-remove-${idx}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--ab-border)" }}>
                <div className="flex justify-between text-sm mb-3">
                  <span style={{ color: "var(--ab-text-muted)" }}>Subtotal</span>
                  <span className="font-mono font-bold" style={{ color: "var(--ab-primary)" }}>${subtotal.toFixed(2)}</span>
                </div>
                <button
                  type="button"
                  disabled={cart.length === 0}
                  onClick={() => setCheckout(true)}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid="shop-checkout-btn"
                >Checkout</button>
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Checkout modal */}
      {checkout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(4px)" }} data-testid="shop-checkout-modal" onClick={() => !coBusy && setCheckout(false)}>
          <form onSubmit={submitReservation} className="surface p-6 max-w-lg w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Checkout</div>
            <h3 className="font-display text-2xl font-bold mb-4">Reserve Your Cart</h3>
            <p className="text-sm mb-4" style={{ color: "var(--ab-text-muted)" }}>We'll confirm the items + pickup window by email before anything is final.</p>

            <div className="flex gap-1 mb-4" style={{ borderBottom: "1px solid var(--ab-border)" }}>
              <button type="button" onClick={() => setCoMode("existing")} className="px-3 py-2 text-xs uppercase tracking-widest font-bold transition-colors" style={{ color: coMode === "existing" ? "var(--ab-primary)" : "var(--ab-text-dim)", borderBottom: coMode === "existing" ? "2px solid var(--ab-primary)" : "2px solid transparent", marginBottom: "-1px" }} data-testid="shop-co-tab-existing">Existing Member / Guest</button>
              <button type="button" onClick={() => setCoMode("new")} className="px-3 py-2 text-xs uppercase tracking-widest font-bold transition-colors" style={{ color: coMode === "new" ? "var(--ab-primary)" : "var(--ab-text-dim)", borderBottom: coMode === "new" ? "2px solid var(--ab-primary)" : "2px solid transparent", marginBottom: "-1px" }} data-testid="shop-co-tab-new">Become AB Member</button>
            </div>

            {coMode === "existing" ? (
              <div className="space-y-3">
                <div>
                  <label className="label-eyebrow block mb-2">AB Member # (phone)</label>
                  <input className="input-terminal" placeholder="555 123 4567 — leave blank to checkout as a guest" value={coPhone} onChange={(e) => setCoPhone(e.target.value)} data-testid="shop-co-member-phone" />
                </div>

                {/* Member perks (live-fetched when phone entered) */}
                {perksLoading && <div className="text-xs inline-flex items-center gap-2" style={{ color: "var(--ab-text-dim)" }} data-testid="shop-perks-loading"><Loader2 size={12} className="animate-spin" /> Looking up perks…</div>}
                {perksError && <div className="text-xs" style={{ color: "var(--ab-warning)" }} data-testid="shop-perks-error">{perksError}</div>}
                {perks && (
                  <div className="surface p-4 space-y-3" style={{ background: "var(--ab-surface-2)" }} data-testid="shop-perks-panel">
                    <div className="flex items-center justify-between">
                      <div className="label-eyebrow inline-flex items-center gap-2" style={{ color: "var(--ab-primary)" }}><Sparkles size={12} /> Hi {perks.first_name}</div>
                      <div className="text-[11px] font-mono" style={{ color: "var(--ab-text-dim)" }}>
                        {perks.points.toLocaleString()} µPts · ${perks.trade_credit} credit
                      </div>
                    </div>
                    <div>
                      <label className="label-eyebrow block mb-2">Redeem µPoints</label>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
                        <button type="button" onClick={() => setPointsDiscount(0)} className="px-2 py-2 text-xs border transition-colors" style={{ borderColor: pointsDiscount === 0 ? "var(--ab-primary)" : "var(--ab-border)", background: pointsDiscount === 0 ? "rgba(255,209,0,0.1)" : "transparent" }} data-testid="shop-perks-tier-0">None</button>
                        {perks.discount_tiers.map((t) => {
                          const ok = perks.points >= t.points && subtotal >= t.amount;
                          const active = pointsDiscount === t.amount;
                          return (
                            <button key={t.amount} type="button" disabled={!ok} onClick={() => setPointsDiscount(t.amount)} className="px-2 py-2 text-xs border transition-colors" style={{ borderColor: active ? "var(--ab-primary)" : "var(--ab-border)", background: active ? "rgba(255,209,0,0.1)" : "transparent", opacity: ok ? 1 : 0.4, cursor: ok ? "pointer" : "not-allowed" }} data-testid={`shop-perks-tier-${t.amount}`} title={ok ? "" : "Not enough µPoints or cart too small"}>
                              <div className="font-bold font-mono">${t.amount} off</div>
                              <div className="text-[10px]" style={{ color: "var(--ab-text-dim)" }}>{t.points.toLocaleString()} pts</div>
                            </button>
                          );
                        })}
                      </div>
                      {availableTiers.length === 0 && <div className="text-[11px] mt-2" style={{ color: "var(--ab-text-dim)" }}>No redemption tiers available yet — keep shopping or add more µPoints to your account.</div>}
                    </div>
                    {perks.trade_credit > 0 && (
                      <div>
                        <label className="label-eyebrow block mb-2 inline-flex items-center gap-2"><BadgeDollarSign size={12} /> Apply Trade Credit (max ${maxCredit})</label>
                        <div className="flex items-center gap-2">
                          <input type="number" min={0} max={maxCredit} step={1} className="input-terminal flex-1" value={creditUsed} onChange={(e) => setCreditUsed(Math.max(0, Math.min(maxCredit, parseInt(e.target.value || "0"))))} data-testid="shop-perks-credit" />
                          <button type="button" className="btn-secondary px-3 py-2 text-xs" onClick={() => setCreditUsed(maxCredit)} data-testid="shop-perks-credit-max">Max</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-[11px]" style={{ color: "var(--ab-text-dim)" }}>Not a member? Use the form on the right (Become AB Member) or leave the field blank to checkout as a guest — we&apos;ll just need your name + email below.</p>
                <div>
                  <label className="label-eyebrow block mb-2">Name (guest)</label>
                  <input className="input-terminal" placeholder="Optional if member # supplied" value={coGuestName} onChange={(e) => setCoGuestName(e.target.value)} data-testid="shop-co-guest-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-eyebrow block mb-2">Email (guest)</label>
                    <input type="email" className="input-terminal" placeholder="Required if not a member" value={coGuestEmail} onChange={(e) => setCoGuestEmail(e.target.value)} data-testid="shop-co-guest-email" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">Phone (guest)</label>
                    <input className="input-terminal" placeholder="Optional" value={coGuestPhone} onChange={(e) => setCoGuestPhone(e.target.value)} data-testid="shop-co-guest-phone" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input required className="input-terminal" placeholder="First name" value={coSignup.first_name} onChange={(e) => setCoSignup((p) => ({ ...p, first_name: e.target.value }))} data-testid="shop-co-new-first" />
                  <input required className="input-terminal" placeholder="Last name" value={coSignup.last_name} onChange={(e) => setCoSignup((p) => ({ ...p, last_name: e.target.value }))} data-testid="shop-co-new-last" />
                </div>
                <input required className="input-terminal" placeholder="Phone (your account #)" value={coSignup.phone} onChange={(e) => setCoSignup((p) => ({ ...p, phone: e.target.value }))} data-testid="shop-co-new-phone" />
                <input required type="email" className="input-terminal" placeholder="Email" value={coSignup.email} onChange={(e) => setCoSignup((p) => ({ ...p, email: e.target.value }))} data-testid="shop-co-new-email" />
                <select className="input-terminal" value={coSignup.birth_month} onChange={(e) => setCoSignup((p) => ({ ...p, birth_month: e.target.value }))} data-testid="shop-co-new-birth">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <p className="text-[11px] inline-flex items-center gap-1" style={{ color: "var(--ab-text-dim)" }}>
                  <UserPlus size={11} /> We'll create a free AB Member account before submitting your reservation.
                </p>
              </div>
            )}

            <div className="mt-4">
              <label className="label-eyebrow block mb-2">Notes for us (optional)</label>
              <textarea rows={2} className="input-terminal" value={coNote} onChange={(e) => setCoNote(e.target.value)} data-testid="shop-co-note" />
            </div>

            <div className="mt-5 pt-4 border-t flex items-end justify-between" style={{ borderColor: "var(--ab-border)" }}>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between gap-6"><span style={{ color: "var(--ab-text-muted)" }}>Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span></div>
                {pointsDiscount > 0 && <div className="flex justify-between gap-6" style={{ color: "var(--ab-primary)" }}><span>µPoints</span><span className="font-mono">−${pointsDiscount.toFixed(2)}</span></div>}
                {creditUsed > 0 && <div className="flex justify-between gap-6" style={{ color: "var(--ab-primary)" }}><span>Trade credit</span><span className="font-mono">−${creditUsed.toFixed(2)}</span></div>}
                <div className="flex justify-between gap-6 pt-1 border-t mt-1" style={{ borderColor: "var(--ab-border)" }}><span className="label-eyebrow">Total</span><span className="font-mono font-bold text-base" style={{ color: "var(--ab-primary)" }} data-testid="shop-co-total">${finalTotal.toFixed(2)}</span></div>
                {perks && estimatedEarn > 0 && (
                  <div className="inline-flex items-center gap-1 mt-2 px-2 py-1" style={{ background: "rgba(255,209,0,0.1)", color: "var(--ab-primary)" }} data-testid="shop-co-earn-estimate">
                    <Sparkles size={11} />
                    <span className="text-[11px] font-mono">You&apos;ll earn ~{estimatedEarn.toLocaleString()} µPts</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setCheckout(false)} disabled={coBusy} className="btn-secondary" data-testid="shop-co-cancel">Cancel</button>
                <button type="submit" disabled={coBusy} className="btn-primary inline-flex items-center gap-2" data-testid="shop-co-submit">
                  {coBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Submit Reservation
                </button>
              </div>
            </div>
            {coError && <div className="mt-3 text-sm" style={{ color: "var(--ab-danger)" }} data-testid="shop-co-error">{coError}</div>}
          </form>
        </div>
      )}
    </Layout>
  );
}
